import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { GetProductVarconds } from '../../../../assets/interfaces/productInterface'
import ImportIcon from '@mui/icons-material/CloudUpload'
import { UpdatePrices } from '../../../../assets/interfaces/priceInterface'

const StepCompareVarconds = ({ validatedProducts, products }) => {
    const [prices, setPrices] = useState([])
    const [loading, setLoading] = useState(false)
    const [fetchingVarconds, setFetchingVarconds] = useState(false)
    const [resultMessage, setResultMessage] = useState(null)

    useEffect(() => {
        const fetchFilteredProducts = async () => {
            setFetchingVarconds(true)
            try {
                const uniqueProductNames = Array.from(
                    new Set(
                        products
                            .filter(({ productName }) => validatedProducts.includes(productName))
                            .map(({ productName }) => productName)
                    )
                )

                const productVarcondsMap = new Map()
                await Promise.all(
                    uniqueProductNames.map(async (productName) => {
                        const varconds = await GetProductVarconds(productName)
                        productVarcondsMap.set(productName, varconds)
                    })
                )

                const filteredArray = products
                    .filter(
                        ({ type, productName }) =>
                            (type === 'Base unit' || type === 'Option' || type === 'Packing') &&
                            validatedProducts.includes(productName)
                    )
                    .map(({ productName, size, varcond, type, optionNb, kmat, reference, quantity }) => {
                        const productVarconds = productVarcondsMap.get(productName)

                        const priceDb = productVarconds?.find((price) => {
                            if (price.size && (type === 'Base unit' || price.number)) {
                                return (
                                    price.size.toString() === size.toString() &&
                                    (type === 'Base unit' || optionNb.toString() === price.number.toString())
                                )
                            }
                            return false
                        })

                        return {
                            name: productName,
                            size,
                            type,
                            varcond,
                            kmat,
                            optionNb,
                            reference,
                            quantity,
                            kmatDb: priceDb ? priceDb.kmat : null,
                            referenceDb: priceDb ? priceDb.reference : null,
                            quantityDb: priceDb ? priceDb.quantity : null,
                            varcondDb: priceDb ? priceDb.varcond : null,
                            priceId: priceDb ? priceDb.id : null,
                            isCompatible: priceDb ? priceDb.compatible : false,
                        }
                    })

                const uniquePrices = new Set()
                const filteredResult = filteredArray.filter((price) => {
                    if (
                        !uniquePrices.has(price.priceId) &&
                        (price.varcondDb !== price.varcond ||
                            price.kmatDb !== price.kmat ||
                            price.quantityDb !== price.quantity ||
                            price.referenceDb !== price.reference) &&
                        price.isCompatible === true
                    ) {
                        uniquePrices.add(price.priceId)
                        return true
                    }
                    return false
                })

                setPrices(Array.from(filteredResult))
            } catch (error) {
                setResultMessage(`Error fetching varconds: ${error.message}`)
            } finally {
                setFetchingVarconds(false)
            }
        }

        if (validatedProducts.length > 0 && products.length > 0) {
            fetchFilteredProducts()
        }
    }, [validatedProducts, products])
    const handleUpdateVarcondsClick = () => {
        setLoading(true)
        const priceIdSet = new Set()
        const filteredVarconds = prices
            .filter((price) => {
                const hasPriceId = Boolean(price.priceId)
                const hasVarcondChanged =
                    price.varcond && (price.varcondDb !== price.varcond || price.kmatDb !== price.kmat)
                const hasReferenceChanged = price.referenceDb !== price.reference || price.quantityDb !== price.quantity

                if (hasPriceId && (hasVarcondChanged || hasReferenceChanged)) {
                    if (!priceIdSet.has(price.priceId)) {
                        priceIdSet.add(price.priceId)
                        return true
                    }
                }
                return false
            })
            .map((price) => {
                return {
                    id: price.priceId,
                    varcond: price.varcond,
                    kmat: price.kmat,
                    reference: price.reference,
                    quantity: price.quantity,
                }
            })

        if (filteredVarconds.length === 0) {
            setResultMessage('No varconds to update')
            setLoading(false)
            return
        }

        UpdatePrices(filteredVarconds)
            .then(() => {
                setResultMessage(`${filteredVarconds.length} price(s) updated successfully`)
            })
            .catch((error) => {
                setResultMessage(`Error updating prices: ${error.message || error}`)
                console.error('Update error:', error)
            })
            .finally(() => {
                setLoading(false)
                setTimeout(() => {
                    window.location.reload()
                }, 2000)
            })
    }
    function calculateVarcondDifference(nameFilter) {
        const filteredItems = prices && prices.filter((item) => item.name === nameFilter)

        const count = filteredItems.reduce((acc, item) => {
            if (
                (item.varcond && item.varcondDb !== item.varcond) ||
                (item.kmat && item.kmatDb !== item.kmat) ||
                (item.reference && item.referenceDb !== item.reference) ||
                (item.quantity && item.quantityDb !== item.quantity)
            ) {
                return acc + 1
            }
            return acc
        }, 0)

        return count
    }

    if (fetchingVarconds) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='300px'>
                <CircularProgress />
                <Typography variant='h6' component='h2' ml={2}>
                    Fetching varconds...
                </Typography>
            </Box>
        )
    }

    return (
        <>
            <TableContainer style={{ maxHeight: '500px', overflowX: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box style={{ flex: '1' }}>
                    <Typography variant='h6' component='h2'>
                        Varconds of the following machine{validatedProducts.length > 1 && 's'} will be updated
                    </Typography>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Typography variant='h6' component='h2'>
                                        Product Name{validatedProducts.length > 1 && 's'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant='h6' component='h2'>
                                        Status in the database
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {validatedProducts.map((product, index) => (
                                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component='th' scope='row'>
                                        <Typography variant='h6' component='h2'>
                                            {product}
                                        </Typography>
                                    </TableCell>
                                    <TableCell component='th' scope='row'>
                                        <Typography variant='h6' component='h2'>
                                            {calculateVarcondDifference(product)} Varcond
                                            {calculateVarcondDifference(product) > 1 && 's'} will be updated
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Snackbar
                        open={!!resultMessage}
                        autoHideDuration={3000}
                        onClose={() => setResultMessage(null)}
                        message={resultMessage}
                    />
                </Box>

                <Box display='flex' justifyContent='center' m={2}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Button
                            onClick={handleUpdateVarcondsClick}
                            component='label'
                            color='primary'
                            variant='outlined'
                            size='large'
                            startIcon={<ImportIcon />}
                            disabled={prices.length === 0}>
                            Update varconds
                        </Button>
                    )}
                </Box>
            </TableContainer>
        </>
    )
}

export default StepCompareVarconds
